// Renderer error reporter — the ONE path a dashboard crash takes to the server
// (`POST /api/diagnostics/renderer-error` → structured log + ops error event).
//
// Before this module only `<ErrorBoundary>` reported, and only what a boundary
// catches. React's commit-phase errors (e.g. #185 "Maximum update depth
// exceeded") and errors thrown outside React never reach a boundary, so they
// died in the DevTools console — a crash the operator saw with no record of
// it anywhere. React 19's root options and the window's own events hand those
// errors (with the component stack where React has one) to `report`.
//
// Guardrails: one POST per error signature until `resetDedupe` (a render storm
// re-throwing on every remount must not log-bomb the server); fire-and-forget;
// never throws (the reporter must not escalate the crash it reports).
import type { RootOptions } from 'react-dom/client';
import type { RendererErrorSource } from '@onestone/shared/schemas';
import { apiFetch } from '@/lib/api';

export interface RendererErrorReport {
  source: RendererErrorSource;
  error: unknown;
  componentStack?: string | undefined;
}

const reported = new Set<string>();

function describe(error: unknown): { name: string; message: string; stack?: string } {
  if (error instanceof Error) {
    const out: { name: string; message: string; stack?: string } = { name: error.name, message: error.message };
    if (error.stack) out.stack = error.stack;
    return out;
  }
  return { name: 'Error', message: String(error) };
}

/** Report one renderer error. Deduped by `source:name:message`. */
export function report({ source, error, componentStack }: RendererErrorReport): void {
  try {
    const { name, message, stack } = describe(error);
    const signature = `${source}:${name}:${message}`;
    if (reported.has(signature)) return;
    reported.add(signature);
    void apiFetch('/api/diagnostics/renderer-error', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        source,
        message: message || name,
        stack,
        componentStack,
        route: typeof window !== 'undefined' ? window.location.hash || window.location.pathname : undefined,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      }),
    }).catch(() => {
      // Best-effort — with the local server down there is nowhere to report to.
    });
  } catch {
    // Never let the reporter escalate the error it reports.
  }
}

/** Forget reported signatures — a route change is a fresh slate. */
export function resetDedupe(): void {
  reported.clear();
}

/** The `createRoot` options every host passes: React's own error callbacks. */
export const rootErrorOptions: Pick<RootOptions, 'onUncaughtError' | 'onCaughtError' | 'onRecoverableError'> = {
  onUncaughtError: (error, info) => {
    console.error('React uncaught error:', error, info.componentStack);
    report({ source: 'uncaught', error, componentStack: info.componentStack });
  },
  onCaughtError: (error, info) => {
    // The boundary that caught it reports with source 'boundary' and shows its
    // recovery UI; this callback keeps React's default console output only.
    console.error('React caught error:', error, info.componentStack);
  },
  onRecoverableError: (error, info) => {
    report({ source: 'recoverable', error, componentStack: info.componentStack });
  },
};

/** Listen for errors thrown outside React (event handlers, timers, promises). Idempotent. */
let listening = false;
export function listenForWindowErrors(): void {
  if (listening || typeof window === 'undefined') return;
  listening = true;
  window.addEventListener('error', (event) => {
    report({ source: 'window', error: event.error ?? event.message });
  });
  window.addEventListener('unhandledrejection', (event) => {
    report({ source: 'unhandledrejection', error: event.reason });
  });
}
